import { Component, ChangeDetectionStrategy, signal, computed, inject, effect, OnInit, ChangeDetectorRef, HostListener, ViewChildren, QueryList } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Product, CartItem } from '../../types/product';
import { ProductService } from '../../services/product.service';
import { ReservationService } from '../../services/reservation.service';
import { SettingsService } from '../../services/settings.service';
import { MaterialTableComponent } from '../shared/material-table/material-table.component';
import { SortableHeader } from '../../types/table';
import { LanguageService } from '../../services/language.service';
import { AuthenticationService } from '@/src/services/authentication.service';
import { MasterService } from '@/src/services/master.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MemberSearch } from '../pos/member-search/member-search'
import { SelectButtonModule } from 'primeng/selectbutton';
import { Constants } from '@/src/helpers/constants';
import { MemberDetail } from '@/src/components/reservation-management/member-detail/member-detail'
import { ReservationDetail } from '@/src/components/reservation-management/reservation-detail/reservation-detail'
import { PopUpCommonComponent } from "@/src/components/pop-up-common/pop-up-common.component";
import { PaymentMethodSelectorComponent } from "../shared/payment-method-selector/payment-method-selector.component";
import { Router } from '@angular/router';
import { InputNumberModule } from 'primeng/inputnumber';
import { Member } from '@/src/types/membership';
import { PhoneFormatPipe } from '@/src/pipes/phone-format-pipe';
import { ClickOutsideDirective } from '@/src/helpers/ClickOutsideDirective';
import { LanguageService2 } from '@/src/services/language.service2';
import { SplitButtonModule } from 'primeng/splitbutton';
import { stringify } from 'querystring';
interface Group {
  code: string;
  name: string;
  sr_tp: string;
}
@Component({
  selector: 'app-reservation-management',
  standalone: true,
  imports: [CurrencyPipe, FormsModule, TranslateModule, MaterialTableComponent, DatePickerModule, SelectModule, InputTextModule, InputNumberModule,
    MemberSearch, CommonModule, SelectButtonModule, MemberDetail, ReservationDetail, PopUpCommonComponent, PaymentMethodSelectorComponent, PhoneFormatPipe,
    ClickOutsideDirective, SplitButtonModule],
  templateUrl: './reservation-management.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './reservation-management.component.css'
})
export class ReservationManagementComponent implements OnInit {
  productService = inject(ProductService);
  reservationService = inject(ReservationService);
  settingsService = inject(SettingsService);
  translate = inject(TranslateService)
  cdr = inject(ChangeDetectorRef)
  private router = inject(Router);
  auth = inject(AuthenticationService);
  masterService = inject(MasterService)
  languageService = inject(LanguageService2)
  openMemberSearch = signal(false);
  member = signal<any | null>(null);
  isLoading = signal<boolean>(false);
  openMemberDetail = signal<boolean>(false);
  member_noDetail = '';
  openReservationDetail = signal<boolean>(false);
  reservationDetail: any;
  showPaymentMethodSelector = signal<boolean>(false);
  param = {
    P_HQ_CD: '',
    P_STR_CD: '',
    P_TEL_NO: '',
    P_USER_NM: '',
    P_USER_ID: '',
    P_USER_ID_2: '',
    P_SATE_DT: '',
    P_START_DT: '',
    P_END_DT: '',
    P_MEMBER_TP: '',
    P_HP_NO: '',
    P_LCLASS_CD: '',
    SALE_DT: new Date(),
    START_DT: new Date(),
    END_DT: new Date(),
  }

  // Common state
  activeTab = signal<'MGMT' | 'HISTORY'>('MGMT');
  members = signal<Member[]>([]);
  // MGMT Tab State
  products = signal<any[]>([])
  selectedTime = signal<string | null>(null);
  selectedCategory = signal<Group | null>(null);
  selectedGroup = signal<Group | null>(null);
  cart = signal<CartItem[]>([]);
  staffList = new Array();
  selectedStaff = '';
  prepaySearchPhone = signal("");
  prepaySearchName = signal("");
  filteredMembersPhone = signal<Member[]>([]);
  filteredMembersName = signal<Member[]>([]);
  showPhoneDropdown = signal(false);
  showNameDropdown = signal(false);
  timeSlots = [];
  locale: any;

  // HISTORY Tab State
  selectedHistoryRows = signal<string[]>([]);
  reservationHistory = signal<any[]>([]);
  posTabs = signal<[]>([]);

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    $event.preventDefault();
    $event.returnValue = '';
  }

  selectMemberFromPhoneDropdown(member: Member) {
    this.prepaySearchPhone.set(member.phone);
    this.prepaySearchName.set(member.name);
    this.showPhoneDropdown.set(false);
    this.filteredMembersPhone.set([]);
    const mem = {
      membeR_NM: member.name,
      hP_NO: member.phone,
      membeR_NO: member.customerNo
    }
    this.member.set(mem);
    this.resetIdempotencyKey();
  }

  selectMemberFromNameDropdown(member: Member) {
    this.prepaySearchPhone.set(member.phone);
    this.prepaySearchName.set(member.name);
    this.showNameDropdown.set(false);
    this.filteredMembersName.set([]);
    const mem = {
      membeR_NM: member.name,
      hP_NO: member.phone,
      membeR_NO: member.customerNo
    }
    this.member.set(mem);
    this.resetIdempotencyKey();
  }

  onNameSearchInput(value: string) {
    this.prepaySearchName.set(value);

    if (!value || value.length < 1) {
      this.filteredMembersName.set([]);
      this.showNameDropdown.set(false);
      return;
    }

    // Filter members by name
    const filtered = this.members().filter((m) => {
      const query = value.toLowerCase();
      return m.name?.toLowerCase().includes(query);
    });

    this.filteredMembersName.set(filtered.slice(0, 10));
    this.showNameDropdown.set(filtered.length > 0);
    if (filtered.length <= 0) {
      this.member.set(null);
    }
    this.resetIdempotencyKey();
  }

  onPhoneSearchInput(value: string) {
    this.prepaySearchPhone.set(value);

    if (!value || value.length < 2) {
      this.filteredMembersPhone.set([]);
      this.showPhoneDropdown.set(false);
      return;
    }

    // Filter members by phone
    const filtered = this.members().filter((m) => {
      return (
        m.phone.includes(value) || m.phone.replace(/-/g, "").includes(value)
      );
    });
    this.filteredMembersPhone.set(filtered.slice(0, 10));
    this.showPhoneDropdown.set(filtered.length > 0);
    if (filtered.length <= 0) {
      this.member.set(null);
    }
    this.resetIdempotencyKey();
  }

  getDataMember() {
    //SP_STR_GET_MEMBER_LIST
    this.masterService
      .excute_spNoLoading("6a839c6c-298d-11f1-9014-00155d001e02", this.param)
      .then((res: any) => {
        const dataRes = res?.result?.dataRes[0].rows ?? [];
        const mapped = dataRes.map((item: any) => ({
          id: item.membeR_NO, // Use MEMBER_NO as unique ID
          memberId: item.membeR_ID, // Store MEMBER_ID separately
          customerNo: item.membeR_NO,
          name: item.membeR_NM,
          phone: this.formatPhoneNumber(item.hP_NO) || item.hP_NO,
          grade: item.membeR_TP_NM || "",
          acc: item.savE_POINT || 0,
          used: item.usE_POINT || 0,
          avail: item.avaiL_POINT || 0,
          count: item.savE_CNT || 0,
          prepaid: item.savE_CHARGE_AMT || 0,
          joinDate: item.joiN_DT || "",
          smsYn: item.smS_YN || "Y",
        }));
        this.members.set(mapped);
      });
  }

  ngOnInit() {
    this.locale = this.languageService.getLocale();
    this.param.P_HQ_CD = this.auth.getHQCD()
    this.param.P_STR_CD = this.auth.getStrCd()
    this.getDataMember();
    this.loadData();
    this.getUser();
    this.getLevel();
    this.getBookingStatus();
    this.isNextPage.set(false)
    this.getHistory();
  }

  getUser() {
    //SP_STR_GET_USER_LIST
    this.masterService.excute_sp('6a83a011-298d-11f1-9014-00155d001e02', this.param).subscribe((res: any) => {
      if (res?.isOK) {
        this.staffList = res?.result?.dataRes[0]?.rows?.filter((x : any) => x.retirE_FG != '2');
        this.selectedStaff = !!this.staffList?.length ? this.staffList[0].useR_ID : '';
        const curentUser = this.auth.getUserId()
        if (curentUser) {
          const check = this.staffList.some((x: any) => x.useR_ID === curentUser);
          if (check) this.selectedStaff = curentUser
        }
        this.getTime()
      }
    })
  }

  getTime() {
    this.selectedTime.set(null)
    this.param.P_SATE_DT = this.masterService.formatDateYYYYMMDD2(this.param.SALE_DT)
    this.param.P_USER_ID = this.selectedStaff;
    //SP_STR_GET_USER_TIME
    this.masterService.excute_sp('6a83a09e-298d-11f1-9014-00155d001e02', this.param).subscribe((res: any) => {
      if (res?.isOK) {
        this.timeSlots = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  loadData() {
    this.isLoading.set(true)
    setTimeout(() => {
      this.productService.getServices().then((res: any) => {
        if (res?.isOK) {
          if (res?.result?.dataRes) {
            this.posTabs.set(res?.result?.dataRes);
            this.selectCategory(res?.result?.dataRes[0])
            const products = res?.result?.dataRes
              ?.flatMap((e: any) => e.sclassdtOs
                ?.flatMap((s: any) => s.srmasterdtOs)
              ) || [];

            this.products.set(products);
          }
        }
        this.isLoading.set(false)
      })
    }, 500);
  }

  // MGMT Computed
  currentGroups = computed(() => {
    const groupByCate = this.posTabs().filter((l: any) => l.l_CLASS_CD == this.selectedCategory()?.code);
    const groups =
      groupByCate.length > 0
        ? (groupByCate[0] as any).sclassdtOs?.map((p: any) => ({
          s_CLASS_CD: p.s_CLASS_CD,
          s_CLASS_NM: p.s_CLASS_NM,
        }))
        : [];
    return Array.from(new Set(groups));
  });

  displayProducts = computed(() => {
    return this.products().filter((p: any) => p.l_CLASS_CD === this.selectedCategory()?.code && p.s_CLASS_CD === this.selectedGroup()?.code);
  });

  totalWorkTime = computed(() => {
    return this.cart().reduce((total, item) => total + ((item.time || 0) * item.quantity), 0);
  });

  // HISTORY Computed
  filteredHistory = computed(() => {
    return this.reservationHistory()
  });

  historyHeaders = computed<SortableHeader[]>(() => {
    return [
      // { key: 'select', label: '', sortable: false },
      { key: 'no', label: this.translate.instant('REVERSTR0015'), sortable: true },
      { key: 'reservatioN_DT', label: this.translate.instant('REVERSTR0019'), sortable: true },
      { key: 'servicE_NM', label: this.translate.instant('REVERSTR0020'), sortable: true },
      { key: 'salE_UPRC', label: this.translate.instant('REVERSTR0021'), sortable: true },
      { key: 'membeR_NM', label: this.translate.instant('REVERSTR0016'), sortable: true },
      { key: 'hP_NO', label: this.translate.instant('REVERSTR0017'), sortable: true },
      { key: 'membeR_TP_NM', label: this.translate.instant('REVERSTR0018'), sortable: true },
      // { key: 'savE_CHARGE_AMT', label: this.translate.instant('REVERSTR0022'), sortable: true },
      { key: 'emP_NM', label: this.translate.instant('REVERSTR0060'), sortable: true },
      { key: 'reservatioN_FG_NM', label: this.translate.instant('REVERSTR0023'), sortable: true },
      { key: 'action', label: this.translate.instant('REVERSTR0024'), sortable: false },
    ];
  });

  allHistorySelected = computed(() => {
    return this.reservationHistory().length > 0 && this.selectedHistoryRows().length === this.reservationHistory().length;
  });

  // MGMT Methods
  addToCart(product: any, isBind: boolean = false) {
    this.resetIdempotencyKey();
    this.cart.update(currentCart => {
      const existingItem = currentCart.find((i: any) => i.servicE_CD === product.servicE_CD && i.l_CLASS_CD === product.l_CLASS_CD && i.s_CLASS_CD === product.s_CLASS_CD);
      product.l_ClassName = this.selectedCategory()?.name;
      product.s_ClassName = this.selectedGroup()?.name;
      if (isBind) {
        product.l_ClassName = product.l_CLASS_NM;
        product.s_ClassName = product.s_CLASS_NM;
        product.quantity = product.salE_QTY;
        product.price = product.salE_UPRC;
        product.sr_tp = product.sR_TP;
        return [...currentCart, { ...product }];
      }
      if (existingItem) {
        return currentCart.map((i: any) => i.servicE_CD === product.servicE_CD ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...currentCart, { ...product, quantity: 1, sr_tp: this.selectedCategory()?.sr_tp }];
    });
  }

  updateQuantity(product: any, delta: number) {
    this.resetIdempotencyKey();
    this.cart.update(prev => prev.map((item: any) => {
      if (item.servicE_CD === product.servicE_CD && item.l_CLASS_CD === product.l_CLASS_CD && item.s_CLASS_CD === product.s_CLASS_CD) {
        return { ...item, quantity: Math.max(0, item.quantity + delta) };
      }
      return item;
    }).filter(item => item.quantity > 0));
  }

  updateQuantityInput(product: any, delta: number) {
    this.productService.resetIdempotencyKey();
    this.cart.update(prev => prev.map((item: any) => {
      if (item.servicE_CD === product.servicE_CD && item.l_CLASS_CD === product.l_CLASS_CD && item.s_CLASS_CD === product.s_CLASS_CD) {
        return { ...item, quantity: Math.max(0, delta) };
      }
      return item;
    }).filter(item => item.quantity > 0));
  }

  removeFromCart(product: any) {
    this.resetIdempotencyKey();
    this.cart.update(prev =>
      prev.filter((item: any) =>
        !(
          item.servicE_CD === product.servicE_CD &&
          item.l_CLASS_CD === product.l_CLASS_CD &&
          item.s_CLASS_CD === product.s_CLASS_CD
        )
      )
    );
  }

  selectGroup(group: any) {
    const g = {
      code: group?.s_CLASS_CD,
      name: group?.s_CLASS_NM,
      sr_tp: '1'
    }
    this.selectedGroup.set(g);
  }
  selectCategory(category: any) {
    this.selectedCategory.set({
      name: category?.l_CLASS_NM,
      code: category?.l_CLASS_CD,
      sr_tp: category?.sR_TP
    });
    // Explicitly set the group to the first available one for the new category.
    const groups = this.currentGroups() as any;
    if (groups.length > 0) {
      this.selectGroup(groups[0])
    }
  }

  // HISTORY Methods
  toggleHistoryRow(no: string) {
    this.selectedHistoryRows.update(selected =>
      selected[0] === no ? [] : [no]
    );
  }

  toggleSelectAllHistory() {
    if (this.allHistorySelected()) {
      this.selectedHistoryRows.set([]);
    } else {
      this.selectedHistoryRows.set(this.reservationHistory().map((i: any) => i.no));
    }
  }

  search() {
    this.resetIdempotencyKey();
    this.getTime()
  }

  reservation() {
    if (this.prepaySearchPhone() === '') {
      this.masterService.showError(this.translate.instant('REVERSTR0073'))
      return;
    }

    if (this.cart().length === 0) {
      this.masterService.showError(this.translate.instant('REVERSTR0035'))
      return;
    }

    if (!this.selectedTime()) {
      this.masterService.showError(this.translate.instant('REVERSTR0036'))
      return;
    }

    if (this.selectedStaff === '') {
      this.masterService.showError(this.translate.instant('REVERSTR0033'))
      return;
    }

    if (!this.param.SALE_DT) {
      this.masterService.showError(this.translate.instant('REVERSTR0034'))
      return;
    }

    const checkMainservice = this.cart().some(x => x.sr_tp === '0');
    if (!checkMainservice) {
      this.masterService.showError(this.translate.instant('ORDERSTR0010'))
      return;
    }

    const param = {
      SERVICES: this.cart(),
      SLOT_TIME: this.selectedTime()?.slice(0, 5),
      USER_ID: this.selectedStaff,
      MEMBER_NO: this.member()?.membeR_NO ?? '',
      MEMBER_TEL_NO: this.prepaySearchPhone(),
      MEMBER_NM: this.prepaySearchName(),
      RESERVATION_NO: this.isEdit ? this.reservationEdit?.reservatioN_NO : '',
      SALE_DT: this.masterService.formatDateYYYYMMDD2(this.param.SALE_DT)
    }
    this.reservationService.addReservation(param).subscribe((res: any) => {
      if (res?.isOK || res?.IsOK) {
        const message = this.isEdit ? 'REVERSTR0052' : 'REVERSTR0026'
        this.masterService.showSuccess(this.translate.instant(message))
        this.cart.set([])
        this.member.set(null);
        this.prepaySearchPhone.set('');
        this.prepaySearchName.set('');
        this.isEdit = false;
        this.slotTimeEdit = '';
        this.reservationEdit = null;
        this.getTime();
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages || res?.ErrorMessages))
      }
    })
  }

  // history
  statuses = new Array()
  statusesUpdate = [];
  selectedStatus = '';
  P_PAGEROW = Constants.P_PAGEROW;
  totalCntHistory = signal(0);
  isNextPage = signal(false)
  getBookingStatus() {
    this.masterService.getCommNoLoading('ST19').then((res: any) => {
      if (res?.isOK) {
        this.statuses = res?.result?.dataRes[0]?.rows;
        this.statusesUpdate = res?.result?.dataRes[0]?.rows.filter((x: any) => x?.comM_D_CD !== "2");
      }
    })
  }

  levels = [];
  getLevel() {
    //SP_STR_GET_MEMBER_LVL
    this.masterService.excute_sp('6a839ca0-298d-11f1-9014-00155d001e02', this.param).subscribe((res: any) => {
      if (res?.isOK) {
        this.levels = res?.result?.dataRes[0]?.rows;
        this.cdr.detectChanges();
      }
    })
  }

  nextPage(page: number) {
    this.isNextPage.set(true)
    this.getHistory(page);
  }

  getHistory(P_PAGEINDEX: number = 1) {
    this.param.P_END_DT = this.masterService.formatDateYYYYMMDD2(this.param.END_DT);
    this.param.P_START_DT = this.masterService.formatDateYYYYMMDD2(this.param.START_DT);
    const param = {
      P_END_DT: this.param.P_END_DT,
      P_START_DT: this.param.P_START_DT,
      P_HQ_CD: this.param.P_HQ_CD,
      P_STR_CD: this.param.P_STR_CD,
      P_USER_ID: this.param.P_USER_ID_2,
      P_LCLASS_CD: this.param.P_LCLASS_CD,
      P_MEMBER_TP: this.param.P_MEMBER_TP,
      P_HP_NO: this.param.P_HP_NO.replaceAll("-", ""),
      P_RESERVATION_FG: this.selectedStatus.length > 0 ? JSON.stringify(this.selectedStatus) : "",
      P_PAGEINDEX: P_PAGEINDEX,
      P_PAGEROW: this.P_PAGEROW
    }
    //SP_RESERVATION_LIST
    this.masterService.excute_sp('6a839b2e-298d-11f1-9014-00155d001e03', param).subscribe((res: any) => {
      if (res?.isOK) {
        const startIndex = (P_PAGEINDEX - 1) * this.P_PAGEROW;
        const newData = res?.result?.dataRes[1]?.rows || [];
        this.totalCntHistory.set(res?.result?.dataRes[0]?.rows[0]?.totalcnt)
        newData.forEach((element: any, index: number) => {
          element.no = String(startIndex + index + 1).padStart(2, '0');
          element.temp = false;
          element.actions = [];

          element.actions.push({
            label: this.translate.instant('REVERSTR0077'),
            icon: 'pi pi-info',
            command: () => this.openReserDetail(element)
          });

          element.actions.push({
            label: this.translate.instant('REVERSTR0078'),
            icon: 'pi pi-user',
            command: () => this.openMemberDetails(element.membeR_NO)
          });

          if (element.reservatioN_FG == '0') {
            element.actions.push({
              label: this.translate.instant('REVERSTR0079'),
              icon: 'pi pi-file-edit',
              command: () => this.bindDetail(element)
            });
          }

          if (['0', '1'].includes(element.reservatioN_FG)) {
            element.actions.push({
              label: this.translate.instant('REVERSTR0080'),
              icon: 'pi pi-credit-card',
              command: () => this.payment(element)
            });
          }
        });

        this.reservationHistory.set(newData);
        this.cdr.detectChanges();
      }
    })
  }

  @ViewChildren('sb') menus!: QueryList<any>;

  onOpenMenu(current: any) {
    // console.log(current)
    this.menus.forEach(menu => {
      if (menu !== current) {
        menu.menu?.hide();
      }
    });
  }

  selectTime(time: any) {
    this.selectedTime.set(time.slot_time);
    this.resetIdempotencyKey()
  }

  openMemberDetails(memberno: string) {
    this.member_noDetail = memberno;
    this.openMemberDetail.set(true)
  }

  openReserDetail(reservationDetail: any) {
    this.reservationDetail = reservationDetail;
    this.openReservationDetail.set(true);
  }

  resetIdempotencyKey() { this.reservationService.resetIdempotencyKey(); }

  onStatusChange(value: string, item: any) {
    const found = this.statuses.find(s => s.comM_D_CD === value);
    if (found) {
      item.reservatioN_FG_NM = found.comM_D_NM;
      item.temp = true;
    }
  }

  reset() {
    this.selectedStatus = '';
    this.param.P_LCLASS_CD = '';
    this.param.P_MEMBER_TP = '';
    this.param.P_HP_NO = '';
    this.param.P_USER_ID_2 = '';
    this.param.START_DT = this.param.END_DT = new Date();
    this.isNextPage.set(false)
    this.getHistory();
  }

  showConfirmUpdate = signal<boolean>(false);

  onShowConfirmUpdate() {
    const reservation = this.reservationHistory().filter((x: any) => x.no === this.selectedHistoryRows()[0])[0] as any;
    if (reservation?.reservatioN_FG == '1') {
      this.showConfirmUpdate.set(false);
      this.updateStatus();
      return;
    }
    if (reservation?.reservatioN_FG == '2') {
      this.masterService.showError(this.translate.instant('REVERSTR0072'));
      return;
    }

    if (reservation?.reservatioN_FG == '3' && reservation?.temp == false) {
      this.masterService.showError(this.translate.instant('REVERSTR0071'));
      return;
    }
    if (reservation?.reservatioN_FG == '3') {
      this.showConfirmUpdate.set(true);
      return;
    }
  }

  updateStatus() {
    this.showConfirmUpdate.set(false);
    const reservation = this.reservationHistory().filter((x: any) => x.no === this.selectedHistoryRows()[0])[0] as any;
    if (reservation?.reservatioN_FG == '2') {
      this.showPaymentMethodSelector.set(true);
      return;
    }
    if (!reservation) return;
    const param = {
      SALE_DT: reservation?.reservatioN_DT,
      RESERVATION_NO: reservation?.reservatioN_NO,
      RESERVATION_FG: reservation?.reservatioN_FG
    }

    this.reservationService.updateStatus(param).subscribe((res: any) => {
      if (res?.isOK) {
        let message = 'REVERSTR0049';
        if (reservation?.reservatioN_FG == 2) {
          message = 'REVERSTR0055';
        }
        if (reservation?.reservatioN_FG == 3) {
          message = 'REVERSTR0050';
        }
        this.masterService.showSuccess(this.translate.instant(message));
        this.selectedHistoryRows.set([])
        this.isNextPage.set(false)
        this.getHistory();
      }
      else {
        this.masterService.showError(Constants.showError(res?.errorMessages))
      }
    })
  }

  onMemberNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.member.update(m => m ? { ...m, hP_NO: '', membeR_NM: input.value, membeR_NO: '' } : m);
    this.resetIdempotencyKey();
    // this.prepaySearchPhone.set('');
  }

  onPhoneInputSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');

    if (input.value.length <= 10) {
      input.value = input.value.replace(/(\d{3})(\d{3})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    } else {
      input.value = input.value.substring(0, 11);
      input.value = input.value.replace(/(\d{3})(\d{4})(\d{0,4})/, (_, a, b, c) => {
        return c ? `${a}-${b}-${c}` : `${a}-${b}`;
      });
    }
    if (this.activeTab() == "MGMT") {
      this.member.update(m => m ? { ...m, hP_NO: input.value, membeR_NM: '', membeR_NO: '' } : m);
      this.prepaySearchPhone.set(input.value);
      this.resetIdempotencyKey();
    }
    else {
      this.param.P_HP_NO = input.value;
    }
    // this.prepaySearchName.set('');
  }

  onSelectMemberSearch(member: any) {
    this.prepaySearchPhone.set(this.formatPhoneNumber(member.hP_NO));
    this.prepaySearchName.set(member.membeR_NM);
    this.member.set(member)
  }
  isEdit: boolean = false;
  reservationEdit: any;
  slotTimeEdit = '';
  bindDetail(reservation: any) {
    // console.log(reservation)
    this.reservationEdit = reservation;
    this.isEdit = true;
    const staff = this.staffList.filter(x => x.emP_NO === reservation.emP_NO);
    this.selectedStaff = staff[0].useR_ID;
    // this.masterService.formatDate
    this.param.SALE_DT = this.masterService.formatStringDateToDate(reservation.reservatioN_DT);
    this.getMember(reservation.membeR_NO)
    this.getTime();
    this.slotTimeEdit = this.masterService.formatTime(reservation.reservatioN_START);
    this.selectedTime.set(this.slotTimeEdit)
    this.getDetail(reservation);
    this.activeTab.set('MGMT');
  }

  cancelUpdate() {
    this.isEdit = false;
    this.reservationEdit = null;
    this.cart.set([])
    this.member.set(null);
    this.prepaySearchName.set('');
    this.prepaySearchPhone.set('');
    this.activeTab.set('HISTORY');
  }


  getMember(member_no: string) {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_MEMBER_NO: member_no
    }
    //SP_STR_GET_MEMBER_INFO
    this.masterService.excute_sp('6a839c37-298d-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        const data = res?.result?.dataRes[0]?.rows[0];
        const filtered = this.members().filter((m) => {
          return (
            m.phone.includes(data.hP_NO) || m.phone.replace(/-/g, "").includes(data.hP_NO)
          );
        });
        const mem = {
          membeR_NM: data.membeR_NM,
          hP_NO: data.hP_NO,
          membeR_NO: data.membeR_NO
        }
        this.prepaySearchPhone.set(this.formatPhoneNumber(data.hP_NO));
        if (filtered) {
          this.prepaySearchName.set(filtered[0]?.name);
        }
        this.member.set(mem);
      }
    })
  }

  getDetail(reservation: any) {
    const param = {
      P_HQ_CD: this.auth.getHQCD(),
      P_STR_CD: this.auth.getStrCd(),
      P_RESERVATION_DT: reservation.reservatioN_DT,
      P_RESERVATION_NO: reservation.reservatioN_NO
    }
    //SP_RESERVATION_DETAIL
    this.masterService.excute_sp('6a839afa-298d-11f1-9014-00155d001e02', param).subscribe((res: any) => {
      if (res?.isOK) {
        this.cart.set([])
        const data = res?.result?.dataRes[0]?.rows;
        data.forEach((element : any) => {
          this.addToCart(element, true);
        });
      }
    })
  }

  payment(data: any) {
    this.router.navigate(['pos'], { queryParams: { reservatioN_DT: data.reservatioN_DT, reservatioN_NO: data.reservatioN_NO } })
  }

  onPaymentCompleted(result: any) {
    // Map payment method to API format
    let paymentMethodApi: 'CASH' | 'CARD' | 'EASYPAY' | 'BANK' = 'CASH';
    switch (result.method) {
      case 'Card':
        paymentMethodApi = 'CARD';
        break;
      case 'Cash':
        paymentMethodApi = 'CASH';
        break;
      case 'EasyPay':
        paymentMethodApi = 'EASYPAY';
        break;
      case 'Points':
        paymentMethodApi = 'BANK'; // Points uses BANK type
        break;
    }
  }

  // Format phone number to XXX-XXXX-XXXX (10 or 11 digits)
  formatPhoneNumber(phone: string): string {
    if (!phone) return "";
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length === 10) {
      // 3-3-4
      return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    } else if (digits.length === 11) {
      // 3-4-4
      return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    }
    return "";
  }

  onStartDateChange() {
    const start = this.param.START_DT
    const end = this.param.END_DT

    if (start && end && end < start) {
      this.param.END_DT = new Date(start);
    }
  }

  onEndDateChange() {
    const start = this.param.START_DT
    const end = this.param.END_DT

    if (start && end && end < start) {
      this.param.START_DT = new Date(end);
    }
  }
}
